/**
 * Advanced Primitives for CAD
 * Helix, Prism, Ellipsoid, and other complex primitive shapes
 */

class AdvancedPrimitives {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Create helix (for threads, springs, etc.)
   */
  createHelix(radius, pitch, height, turns) {
    const points = [];
    const segments = turns * 32; // 32 points per turn

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = t * turns * Math.PI * 2;
      const z = t * height;

      points.push(new THREE.Vector3(
        radius * Math.cos(angle),
        radius * Math.sin(angle),
        z
      ));
    }

    // Create tube along helix path
    const curve = new THREE.CatmullRomCurve3(points);
    const tubeRadius = pitch / 10; // Small tube radius
    const geometry = new THREE.TubeGeometry(curve, segments, tubeRadius, 8, false);

    return geometry;
  }

  /**
   * Create regular prism (n-sided)
   */
  createPrism(radius, height, sides) {
    const shape = new THREE.Shape();

    // Create regular polygon base
    for (let i = 0; i <= sides; i++) {
      const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
      const x = radius * Math.cos(angle);
      const y = radius * Math.sin(angle);

      if (i === 0) {
        shape.moveTo(x, y);
      } else {
        shape.lineTo(x, y);
      }
    }

    // Extrude to create prism
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: height,
      bevelEnabled: false
    });

    return geometry;
  }

  /**
   * Create ellipsoid (stretched sphere)
   */
  createEllipsoid(radiusX, radiusY, radiusZ) {
    // Create sphere and scale it
    const geometry = new THREE.SphereGeometry(1, 32, 32);

    // Scale to create ellipsoid
    geometry.scale(radiusX, radiusY, radiusZ);

    return geometry;
  }

  /**
   * Create spiral (planar spiral)
   */
  createSpiral(innerRadius, outerRadius, turns, height = 0) {
    const points = [];
    const segments = turns * 32;

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = t * turns * Math.PI * 2;
      const r = innerRadius + (outerRadius - innerRadius) * t;

      points.push(new THREE.Vector3(
        r * Math.cos(angle),
        r * Math.sin(angle),
        t * height
      ));
    }

    const curve = new THREE.CatmullRomCurve3(points);
    const geometry = new THREE.TubeGeometry(curve, segments, 0.05, 8, false);

    return geometry;
  }

  /**
   * Create gear tooth profile (involute curve)
   */
  createInvoluteGearToothProfile(module, teeth, pressureAngle) {
    const pitchDiameter = module * teeth;
    const pitchRadius = pitchDiameter / 2;
    const baseRadius = pitchRadius * Math.cos(pressureAngle);
    const addendum = module;
    const dedendum = 1.25 * module;
    const outerRadius = pitchRadius + addendum;
    const rootRadius = pitchRadius - dedendum;

    const toothAngle = (2 * Math.PI) / teeth;

    const shape = new THREE.Shape();

    // Generate one tooth profile using involute curve
    const involutePoints = [];
    const steps = 20;

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const r = baseRadius + (outerRadius - baseRadius) * t;
      const theta = Math.sqrt((r * r) / (baseRadius * baseRadius) - 1);

      const x = r * Math.cos(theta);
      const y = r * Math.sin(theta);

      involutePoints.push({ x, y });
    }

    // Build tooth shape
    shape.moveTo(rootRadius, 0);

    involutePoints.forEach((pt, i) => {
      shape.lineTo(pt.x, pt.y);
    });

    // Mirror for other side of tooth
    for (let i = involutePoints.length - 1; i >= 0; i--) {
      const pt = involutePoints[i];
      shape.lineTo(pt.x, -pt.y);
    }

    shape.lineTo(rootRadius, 0);

    return shape;
  }

  /**
   * Create regular star shape
   */
  createStar(outerRadius, innerRadius, points) {
    const shape = new THREE.Shape();

    for (let i = 0; i <= points * 2; i++) {
      const angle = (i * Math.PI) / points - Math.PI / 2;
      const r = i % 2 === 0 ? outerRadius : innerRadius;
      const x = r * Math.cos(angle);
      const y = r * Math.sin(angle);

      if (i === 0) {
        shape.moveTo(x, y);
      } else {
        shape.lineTo(x, y);
      }
    }

    return shape;
  }

  /**
   * Create spring (helical spring)
   */
  createSpring(coilRadius, wireRadius, coils, pitch) {
    const height = coils * pitch;
    const segments = coils * 32;
    const points = [];

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = t * coils * Math.PI * 2;
      const z = t * height;

      points.push(new THREE.Vector3(
        coilRadius * Math.cos(angle),
        coilRadius * Math.sin(angle),
        z
      ));
    }

    const curve = new THREE.CatmullRomCurve3(points);
    const geometry = new THREE.TubeGeometry(curve, segments, wireRadius, 16, false);

    return geometry;
  }

  /**
   * Create thread (helical thread on cylinder)
   */
  createThread(diameter, pitch, length, threadDepth) {
    // Simplified thread - use helix with triangular profile
    const radius = diameter / 2;
    const turns = length / pitch;

    // Create helix path
    const points = [];
    const segments = turns * 32;

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = t * turns * Math.PI * 2;
      const z = t * length;

      points.push(new THREE.Vector3(
        radius * Math.cos(angle),
        radius * Math.sin(angle),
        z
      ));
    }

    const curve = new THREE.CatmullRomCurve3(points);

    // Create triangular thread profile
    const threadProfile = new THREE.Shape();
    threadProfile.moveTo(0, 0);
    threadProfile.lineTo(threadDepth, pitch / 4);
    threadProfile.lineTo(0, pitch / 2);
    threadProfile.lineTo(0, 0);

    // Extrude along helix
    const geometry = new THREE.ExtrudeGeometry(threadProfile, {
      extrudePath: curve,
      steps: segments,
      bevelEnabled: false
    });

    return geometry;
  }
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { AdvancedPrimitives };
}
